import React from 'react';

import { Box, Chip, LinearProgress, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';

import LoyaltyCoinIcon from '~/components/Icons/LoyaltyCoinIcon';

import { DashboardEmployee } from '~/hooks/apis/business-traveller/useBusinessDashboard';
import { useLebtSilverGrants } from '~/hooks/apis/business-traveller/useLebtSilverGrants';
import {
  useDashboardSocieteSettings,
  useEmployeeSocieteProgress,
} from '~/hooks/apis/business-traveller/useSocieteProgress';

import { getSilverGrantProgress } from '~/utils/businessTraveller/societeProgress';

const money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

interface Props {
  businessId: string;
  employee?: App.BusinessEmployee;
  metrics?: DashboardEmployee;
}

export default function BusinessDashboardEmployeeRow({ businessId, employee, metrics }: Props) {
  const grants = useLebtSilverGrants(businessId);
  const employeeId = employee?.id ?? metrics?.id;
  const grantDate = employeeId ? grants.data?.silverGrantDates[employeeId] : undefined;
  const unknownGrant = employeeId && grants.data?.unknownEmployeeIds.includes(employeeId);
  const settings = useDashboardSocieteSettings(businessId);
  const live = useEmployeeSocieteProgress(businessId, employee);
  const tier = live.data ? live.data.tier : metrics?.tier;
  const eligible = employee?.status === 'ACTIVATED' && !!employee?.customerId && !!employee?.creditCustomerId;
  const progress = live.data ? getSilverGrantProgress(settings.data, live.data.qualifiedOrders) : undefined;
  const hasSilverOrHigher = !!tier && ['silver', 'gold', 'platinum', 'infinite'].includes(tier);
  const error = live.error || settings.error;
  return (
    <TableRow>
      <TableCell>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography fontWeight={600}>
            {employee ? `${employee.firstName} ${employee.lastName}` : 'Employee no longer in current roster'}
          </Typography>
          {tier && (
            <Tooltip title={`Société ${tier}`}>
              <Box component="span" aria-label={`Société ${tier}`}>
                <LoyaltyCoinIcon tier={tier} size={22} />
              </Box>
            </Tooltip>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {employee?.customerId || metrics?.customer_id || 'No linked user'}
        </Typography>
      </TableCell>
      <TableCell>{metrics ? money.format(metrics.ttv) : '—'}</TableCell>
      <TableCell>{metrics ? money.format(metrics.margin) : '—'}</TableCell>
      <TableCell>{metrics?.orders ?? '—'}</TableCell>
      <TableCell sx={{ textTransform: 'capitalize' }}>
        {tier ?? (live.data ? 'Not enrolled' : 'Not available')}
      </TableCell>
      <TableCell>
        {grants.isLoading
          ? 'Loading…'
          : grants.isError
            ? 'Unable to load'
            : grantDate
              ? 'Yes'
              : unknownGrant
                ? 'Unknown'
                : 'No recorded grant'}
      </TableCell>
      <TableCell sx={{ minWidth: 240 }}>
        {grants.isLoading ? (
          <LinearProgress aria-label="Loading Silver grant" />
        ) : grants.isError ? (
          <Typography variant="body2" color="error">
            Unable to load grant history
          </Typography>
        ) : grantDate ? (
          new Date(grantDate).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })
        ) : unknownGrant ? (
          <Typography variant="body2">Grant history incomplete</Typography>
        ) : hasSilverOrHigher ? (
          <Chip size="small" label="Already Silver or above" />
        ) : !eligible ? (
          <Typography variant="body2">
            {!employee
              ? 'No current employee record'
              : employee.status !== 'ACTIVATED'
                ? 'Employee not activated'
                : 'Link a Société account to qualify'}
          </Typography>
        ) : error ? (
          <Tooltip title={error.message}>
            <Typography variant="body2" color="error">
              Unable to load Silver progress
            </Typography>
          </Tooltip>
        ) : live.isLoading || settings.isLoading ? (
          <LinearProgress aria-label="Loading Silver progress" />
        ) : !progress ? (
          <Typography variant="body2">Silver grants not enabled</Typography>
        ) : (
          <Stack gap={0.5}>
            <Typography fontWeight={600}>
              {progress.remaining} more {progress.remaining === 1 ? 'order' : 'orders'} to Silver
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (progress.qualifiedOrders / progress.threshold) * 100)}
              aria-label="Progress to Silver"
            />
            <Typography variant="caption" color="text.secondary">
              {progress.qualifiedOrders} / {progress.threshold} qualifying completed orders this membership year
            </Typography>
            {!live.data?.tier && (
              <Typography variant="caption" color="text.secondary">
                Enrol in Société to receive status.
              </Typography>
            )}
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}
